/**
 * Prop Studio — variants: more than one model for the same prop.
 *
 * A prop's first model lives under its own id (`bike`); further ones are
 * entries of their own keyed `<id>~<n>` (`bike~2`, `bike~3` …), each with its
 * folder, files, bounds and placement, exactly like the first. The page shows
 * them as a strip on the prop; the designer picks one for each staged copy
 * and stages it at THAT model's size (stage.ts), writing the pick into the
 * room export so the render shows the same one; a render of an export with
 * no pick draws one from its seed (render_room.py). Pure: no three, no
 * fetch, so it runs under jest.
 */

import { Bounds, PropTransform, placedBoundsFeet, stagedSizeFeet } from './placement';
import { Library, LibraryEntry, PropStatus, previewUrl, propStatus } from './studioApi';
import type { PropSize } from '../../calculations/staging/kits';

const SUFFIX = /^(.*)~(\d+)$/;
/** A PART of a model — a toolbox on the workbench — is an entry keyed `<owner>+<n>` (`workbench+1`, `workbench~2+1`). */
const PART = /^(.*)\+(\d+)$/;

/** The model a part belongs to: `workbench~2+1` → `workbench~2`; a model's own key is its own owner. */
export function ownerOf(key: string): string {
  const m = PART.exec(key);
  return m ? m[1] : key;
}

export const isPartKey = (key: string): boolean => PART.test(key);

/** A part's number as shown: `workbench+3` → 3. */
export function partNumber(key: string): number {
  const m = PART.exec(key);
  return m ? parseInt(m[2], 10) : 0;
}

/** The library's parts of a model, in number order. */
export function partKeys(library: Library | null | undefined, ownerKey: string): string[] {
  if (!library) return [];
  return Object.keys(library.props)
    .filter((k) => isPartKey(k) && ownerOf(k) === ownerKey)
    .sort((a, b) => partNumber(a) - partNumber(b));
}

/** The key the next part of a model would take: the first free `+n` from 1. */
export function nextPartKey(library: Library | null | undefined, ownerKey: string): string {
  const taken = new Set(partKeys(library, ownerKey).map(partNumber));
  let n = 1;
  while (taken.has(n)) n++;
  return `${ownerKey}+${n}`;
}

/** The prop an entry key belongs to: `bike~2` → `bike`, `workbench~2+1` → `workbench`. */
export function baseId(key: string): string {
  const m = SUFFIX.exec(ownerOf(key));
  return m ? m[1] : ownerOf(key);
}

/** The variant's number as shown: 1 for the prop's own key, n for `~n`. */
export function variantNumber(key: string): number {
  const m = SUFFIX.exec(key);
  return m ? parseInt(m[2], 10) : 1;
}

/** The library's keys for a prop, first one first: `bike`, `bike~2` … (only those the library has). */
export function variantKeys(library: Library | null | undefined, propId: string): string[] {
  if (!library) return [];
  return Object.keys(library.props)
    .filter((k) => !isPartKey(k) && baseId(k) === propId)
    .sort((a, b) => variantNumber(a) - variantNumber(b));
}

/**
 * The key the next model of a prop would take: the prop's own when it has
 * none at all, else the first free `~n` from 2 — never the prop's own key
 * again once there are others (a removed Model 1 stays a gap, so the numbers
 * the user knows the others by do not shift).
 */
export function nextVariantKey(library: Library | null | undefined, propId: string): string {
  const keys = variantKeys(library, propId);
  if (keys.length === 0) return propId;
  const taken = new Set(keys.map(variantNumber));
  let n = 2;
  while (taken.has(n)) n++;
  return `${propId}~${n}`;
}

/** The entry key for a prop's variant number (1 = the prop's own key). */
export function variantKey(propId: string, n: number): string {
  return n <= 1 ? propId : `${propId}~${n}`;
}

/** A prop's status over all its variants: stale if any is, assigned if any has a model, else none. */
export function propStatusAcross(library: Library | null | undefined, propId: string): PropStatus {
  const statuses = variantKeys(library, propId).map((k) => propStatus(library?.props[k]));
  if (statuses.includes('stale')) return 'stale';
  if (statuses.includes('assigned')) return 'assigned';
  return 'none';
}

/** A placed model (a variant, or a part of one) as the designer draws it: its preview, bounds and placement. */
export interface PropModelView {
  key: string;
  /** Where the browser loads the preview .glb from; null when there is none to show. */
  url: string | null;
  modelBounds: Bounds;
  transform: PropTransform;
}

/**
 * One of a prop's models as the designer stages it: the library key, the
 * size to stage and draw the stand-in at (feet: w along the wall, d into the
 * room, h up), and what to draw in the stand-in's place when the preview can
 * be loaded — the model with its parts.
 */
export interface PropVariant {
  key: string;
  size: PropSize;
  /** The box round the placed model and its parts, in the prop's frame (feet: x along, y up, z into the room). */
  box: Bounds;
  model: PropModelView;
  parts: PropModelView[];
}

/** The box round two boxes. */
const union = (a: Bounds | null, b: Bounds): Bounds => (a
  ? { min: [Math.min(a.min[0], b.min[0]), Math.min(a.min[1], b.min[1]), Math.min(a.min[2], b.min[2])], max: [Math.max(a.max[0], b.max[0]), Math.max(a.max[1], b.max[1]), Math.max(a.max[2], b.max[2])] }
  : b);

/**
 * A prop's models the designer can stage: every variant that has a model
 * with bounds and a placement, each at its staged size — the placement's
 * stage box when Prop Studio set one, else the box round the placed model
 * and its parts (a toolbox on the bench counts). Empty when none is placed:
 * the kit's size then applies.
 */
export function propVariants(library: Library | null | undefined, propId: string): PropVariant[] {
  const out: PropVariant[] = [];
  const view = (key: string): PropModelView | null => {
    const e: LibraryEntry | undefined = library?.props[key];
    if (!e?.modelBounds || !e.transform || !e.status?.hasModel) return null;
    return { key, url: e.status.hasGlb ? previewUrl(key, e) : null, modelBounds: e.modelBounds, transform: e.transform };
  };
  for (const key of variantKeys(library, propId)) {
    const model = view(key);
    if (!model) continue;
    const parts = partKeys(library, key).map(view).filter((p): p is PropModelView => !!p);
    let box: Bounds | null = null;
    for (const m of [model, ...parts]) box = union(box, placedBoundsFeet(m.modelBounds, m.transform));
    out.push({ key, size: stagedSizeFeet(box as Bounds, model.transform), box: box as Bounds, model, parts });
  }
  return out;
}
